import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import {
  forwardRef,
  type Ref,
  type SyntheticEvent,
  useImperativeHandle,
  useState
} from 'react';

import CheckPermissions from '@/components/CheckPermissions';

import type {
  ContextMenuItem,
  ContextMenuProps,
  ContextMenuRefType
} from './ContextMenu.types';

const ContextMenu = forwardRef(
  (props: ContextMenuProps, ref: Ref<ContextMenuRefType>) => {
    const { anchorOrigin, transformOrigin, menuList, className, clickHandler } =
      props;
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [open, setOpen] = useState(false);
    const [menuListItems, setMenuListItems] = useState(menuList);
    const showContextMenu = (
      tar: HTMLElement,
      overrideMenuList?: ContextMenuItem[]
    ) => {
      if (overrideMenuList) {
        setMenuListItems(overrideMenuList);
      }
      if (tar) {
        setAnchorEl(tar);
        setOpen(true);
      }
    };

    useImperativeHandle(ref, () => ({ showContextMenu }));

    const handleClose = (event: MouseEvent) => {
      event.stopPropagation();
      setOpen(false);
      clickHandler('');
    };

    const onMenuSelected = (selectedMenu: string) => {
      setOpen(false);
      clickHandler(selectedMenu);
    };

    const anchorOriginProp = anchorOrigin || {
      vertical: 'bottom',
      horizontal: 'right'
    };

    const transformOriginProp = transformOrigin || {
      vertical: 'top',
      horizontal: 'right'
    };

    return (
      <Menu
        className={className}
        id="basic-menu"
        anchorEl={anchorEl}
        anchorOrigin={anchorOriginProp}
        transformOrigin={transformOriginProp}
        open={open}
        onClose={(event: MouseEvent) => handleClose(event)}
        slotProps={{ list: { 'aria-labelledby': 'basic-button' } }}
      >
        {menuListItems.map((menu) => (
          <CheckPermissions
            key={menu.label}
            permissions={menu.permission}
            logic={menu.logic}
          >
            <MenuItem
              disabled={menu?.disabled}
              onClick={(event: SyntheticEvent) => {
                event.stopPropagation();
                onMenuSelected(menu.label);
              }}
            >
              <ListItemIcon>
                <FontAwesomeIcon icon={menu.icon} />
              </ListItemIcon>
              <ListItemText sx={{ minWidth: '6rem' }}>
                {menu.label}
              </ListItemText>
            </MenuItem>
          </CheckPermissions>
        ))}
      </Menu>
    );
  }
);

ContextMenu.displayName = 'ContextMenu';

export default ContextMenu;
